import { useState } from 'react';
import { Link } from 'react-router-dom';
import { LoggingTabs, TabType } from '../components/logging/LoggingTabs';
import { DatePicker } from '../components/ui/DatePicker';
import { getLocalDateString } from '../lib/dateUtils';
import { WeightForm } from '../components/logging/WeightForm';
import { MealForm } from '../components/logging/MealForm';
import { ExerciseForm } from '../components/logging/ExerciseForm';
import { HealthMetricForm } from '../components/logging/HealthMetricForm';
import { MedicalForm } from '../components/logging/MedicalForm';
import { ActivityForm } from '../components/logging/ActivityForm';

/**
 * LoggingPage Component
 *
 * Main logging page with tab navigation for different entry types.
 * Date selection persists across all tabs.
 */
export function LoggingPage() {
	const [activeTab, setActiveTab] = useState<TabType>('makanan');
	const [selectedDate, setSelectedDate] = useState(getLocalDateString());
	const [successMessage, setSuccessMessage] = useState<string | null>(null);

	// Handler for when form submission succeeds (to be used by child forms)
	const handleSuccess = (message: string) => {
		setSuccessMessage(message);
	};

	// Handler to add another entry (clears success message, keeps tab and date)
	const handleAddAnother = () => {
		setSuccessMessage(null);
	};

	return (
		<div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-6 pb-8 sm:pb-6">
			{/* Page Header */}
			<div className="mb-6">
				<h1 className="text-2xl font-bold text-gray-900">Catat Data</h1>
				<p className="text-gray-600 mt-1">
					Pilih kategori dan masukkan data
				</p>
			</div>

			{/* Date Picker - shared across all tabs */}
			<div className="bg-white shadow rounded-lg p-4 mb-4">
				<DatePicker
					value={selectedDate}
					onChange={setSelectedDate}
					label="Tanggal Pencatatan"
					id="logging-date"
				/>
			</div>

			{/* Tab Navigation */}
			<div className="bg-white shadow rounded-lg mb-4">
				<LoggingTabs activeTab={activeTab} onTabChange={setActiveTab} />
			</div>

			{/* Success Message */}
			{successMessage && (
				<div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-4">
					<p className="text-green-800 font-medium">{successMessage}</p>
					<div className="mt-3 flex gap-3">
						<button
							onClick={handleAddAnother}
							className="px-4 py-2 text-sm font-medium text-green-700 bg-green-100 rounded-lg hover:bg-green-200 transition"
						>
							Tambah Lagi
						</button>
						<Link
							to={`/entries?date=${selectedDate}`}
							className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition"
						>
							Lihat Log
						</Link>
					</div>
				</div>
			)}

			{/* Tab Content - Forms */}
			<div className="bg-white shadow rounded-lg p-6">
				{!successMessage && (
					<>
						{activeTab === 'berat' && (
							<WeightForm
								selectedDate={selectedDate}
								onSuccess={handleSuccess}
							/>
						)}
						{activeTab === 'makanan' && (
							<MealForm
								selectedDate={selectedDate}
								onSuccess={handleSuccess}
							/>
						)}
						{activeTab === 'olahraga' && (
							<ExerciseForm
								selectedDate={selectedDate}
								onSuccess={handleSuccess}
							/>
						)}
						{activeTab === 'kesehatan' && (
							<HealthMetricForm
								selectedDate={selectedDate}
								onSuccess={handleSuccess}
							/>
						)}
						{activeTab === 'medis' && (
							<MedicalForm
								selectedDate={selectedDate}
								onSuccess={handleSuccess}
							/>
						)}
						{activeTab === 'aktivitas' && (
							<ActivityForm
								selectedDate={selectedDate}
								onSuccess={handleSuccess}
							/>
						)}
					</>
				)}
			</div>

			{/* Instructions */}
			<div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
				<p className="text-blue-800 text-sm">
					<strong>Petunjuk:</strong> Pilih tab kategori di atas, lalu isi form yang tersedia.
					Tanggal yang dipilih akan digunakan untuk semua pencatatan.
				</p>
			</div>
		</div>
	);
}
